/**
 * The single door between the SPA and the server. Every network call in the product
 * goes through here, so error shape, credential handling and JSON parsing are decided
 * once rather than in each component.
 */

import type {
  CaseSummary,
  CreatedInvite,
  InviteSummary,
  ParticipantRow,
  ParticipantsResponse,
  ParticipantTranscript,
  StudyAnonymity,
  StudyAudience,
  StudyStatus,
  StudySummary,
  ConversationDetail,
  ConversationSummary,
  Dossier,
  Locale,
  MeResponse,
  Suggestion,
  TranscriptionResult,
  TurnEvent,
} from "../../../shared/types";

/** Mirrors the `{ error: { code, message } }` envelope in docs/CONTRACTS.md. */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
  }

  /** True when the request never reached the server, so nothing was written. */
  get isNetwork(): boolean {
    return this.status === 0;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** This deploy has no working voice path. Callers fall back to text in silence. */
  get isVoiceUnavailable(): boolean {
    return this.status === 503 && this.code === "voice_unavailable";
  }
}

/**
 * The message to show a person for a failed call.
 *
 * A 4xx carries a message this product wrote, so it is shown as-is. A 5xx or a
 * transport failure carries whatever a proxy felt like saying — "Internal Server
 * Error" is not something the consultant would ever come out with — so the caller's
 * own sentence replaces it.
 */
export function describe(err: unknown, fallback: string): string {
  return err instanceof ApiError && err.status >= 400 && err.status < 500
    ? err.message
    : fallback;
}

export interface HealthResponse {
  ok: true;
  db: boolean;
  voice: boolean;
}

/**
 * GET /api/config — public, read before anyone signs in. `access` decides whether
 * the landing offers an account at all: "private" means the allowlist is on and
 * sign-up does not exist anywhere in the UI.
 */
export interface ConfigResponse {
  access: "private" | "open";
  voice: boolean;
}

/**
 * Who the consultant is talking to, per case. Written by the server when the intake
 * conversation has learned enough; the shape is CONTRACTS.md Wave 3, verbatim.
 */
export interface Respondent {
  role: "founder" | "employee" | "executive" | "explorer" | "other";
  roleDetail: string | null;
  company: string | null;
  industry: string | null;
  stage: string | null;
  sessionGoal: string | null;
  timeBudgetMin: number | null;
  languages: string[];
  summary: string | null;
}

/** GET /api/cases/:id — the CaseSummary fields plus what the consultant knows. */
export interface CaseDetail extends CaseSummary {
  respondent: Respondent | null;
  research: unknown;
  intakeCompletedAt: string | null;
  documentCount: number;
}

/** One uploaded document, as the rail lists it. */
export interface DocumentSummary {
  id: number;
  filename: string;
  mime: string;
  size: number;
  summary: string | null;
  createdAt: string;
}

export interface TurnBody {
  text: string;
  inputMode: "text" | "voice";
}

export interface RegisterBody {
  email: string;
  password: string;
  displayName?: string;
  locale?: Locale;
}

export interface NewConversationBody {
  /** The business this conversation is about. Omitted, the server uses the active case. */
  caseId?: number;
  language?: Locale;
}

export interface CasePatch {
  name?: string;
  archived?: boolean;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
}

const JSON_HEADERS: Record<string, string> = { "content-type": "application/json" };

/**
 * An abort is the caller's own doing, so it stays a DOMException rather than being
 * dressed up as a failure: callers tell cancellation from breakage by `name`.
 */
function rethrow(err: unknown): never {
  if (err instanceof ApiError) throw err;
  if (err instanceof DOMException && err.name === "AbortError") throw err;
  throw new ApiError("network", err instanceof Error ? err.message : "Request failed", 0);
}

async function readError(res: Response): Promise<ApiError> {
  // A proxy timeout or a crashed process answers with HTML, not our envelope, so a
  // failed parse still has to yield a usable code instead of a second exception.
  let code = `http_${res.status}`;
  let message = res.statusText || "Request failed";
  try {
    const body: unknown = await res.json();
    if (body !== null && typeof body === "object" && "error" in body) {
      const envelope = (body as { error: unknown }).error;
      if (envelope !== null && typeof envelope === "object") {
        const e = envelope as { code?: unknown; message?: unknown };
        if (typeof e.code === "string" && e.code.length > 0) code = e.code;
        if (typeof e.message === "string" && e.message.length > 0) message = e.message;
      }
    }
  } catch {
    // Keep the status-derived code; there was nothing better in the body.
  }
  return new ApiError(code, message, res.status);
}

async function send(path: string, opts: RequestOptions = {}): Promise<Response> {
  let res: Response;
  try {
    res = await fetch(path, {
      method: opts.method ?? "GET",
      credentials: "same-origin",
      headers: opts.body === undefined ? undefined : JSON_HEADERS,
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      signal: opts.signal ?? null,
    });
  } catch (err) {
    rethrow(err);
  }
  if (!res.ok) throw await readError(res);
  return res;
}

async function sendJson<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const res = await send(path, opts);
  return (await res.json()) as T;
}

/**
 * Everything the dossier owns is read per case. A null id is the signed-in default —
 * the server falls back to the active case — but the client passes an explicit id
 * wherever it has one, because its idea of which case is open comes from localStorage
 * and may be older than the server's.
 */
function scoped(path: string, caseId: number | null): string {
  return caseId === null ? path : `${path}?caseId=${caseId}`;
}

// --- Health ----------------------------------------------------------------

export function getHealth(signal?: AbortSignal): Promise<HealthResponse> {
  return sendJson<HealthResponse>("/api/health", { signal });
}

/**
 * Never cached and never authed: the landing reads it before anyone exists. A
 * caller that cannot reach it should fall back to `access: "open"` — the failure
 * mode of a missing config must be today's behaviour, not a locked door.
 */
export function getConfig(signal?: AbortSignal): Promise<ConfigResponse> {
  return sendJson<ConfigResponse>("/api/config", { signal });
}

// --- Identity --------------------------------------------------------------

export function register(body: RegisterBody): Promise<MeResponse> {
  return sendJson<MeResponse>("/api/auth/register", { method: "POST", body });
}

export function login(body: { email: string; password: string }): Promise<MeResponse> {
  return sendJson<MeResponse>("/api/auth/login", { method: "POST", body });
}

export async function logout(): Promise<void> {
  await send("/api/auth/logout", { method: "POST" });
}

export function getMe(signal?: AbortSignal): Promise<MeResponse> {
  return sendJson<MeResponse>("/api/me", { signal });
}

export function updateMe(patch: { displayName?: string; locale?: Locale }): Promise<MeResponse> {
  return sendJson<MeResponse>("/api/me", { method: "PATCH", body: patch });
}

// --- Cases -----------------------------------------------------------------

export function listCases(signal?: AbortSignal): Promise<CaseSummary[]> {
  return sendJson<CaseSummary[]>("/api/cases", { signal });
}

/** Unnamed is the normal case: the first conversation's title names it. */
export function createCase(name?: string): Promise<CaseSummary> {
  return sendJson<CaseSummary>("/api/cases", {
    method: "POST",
    body: name === undefined ? {} : { name },
  });
}

export function updateCase(id: number, patch: CasePatch): Promise<CaseSummary> {
  return sendJson<CaseSummary>(`/api/cases/${id}`, { method: "PATCH", body: patch });
}

export function getCase(id: number, signal?: AbortSignal): Promise<CaseDetail> {
  return sendJson<CaseDetail>(`/api/cases/${id}`, { signal });
}

/**
 * A fresh intake conversation for a case that already has a respondent. The old
 * profile stays in force until the new intake completes and replaces it.
 */
export function redoIntake(caseId: number): Promise<ConversationDetail> {
  return sendJson<ConversationDetail>(`/api/cases/${caseId}/intake`, { method: "POST" });
}

// --- Documents -------------------------------------------------------------

export function listDocuments(caseId: number, signal?: AbortSignal): Promise<DocumentSummary[]> {
  return sendJson<DocumentSummary[]>(`/api/cases/${caseId}/documents`, { signal });
}

/**
 * Waits for the server to have read and summarised the file — uploading is a
 * deliberate act and a few seconds is fine. JSON helpers cannot carry a file, so
 * this posts the form itself the way `transcribe` does.
 */
export async function uploadDocument(caseId: number, file: File): Promise<DocumentSummary> {
  const form = new FormData();
  form.append("file", file, file.name);
  let res: Response;
  try {
    res = await fetch(`/api/cases/${caseId}/documents`, {
      method: "POST",
      credentials: "same-origin",
      body: form,
    });
  } catch (err) {
    rethrow(err);
  }
  if (!res.ok) throw await readError(res);
  return (await res.json()) as DocumentSummary;
}

export async function deleteDocument(id: number): Promise<void> {
  await send(`/api/documents/${id}`, { method: "DELETE" });
}

// --- Conversations ---------------------------------------------------------

export function listConversations(signal?: AbortSignal): Promise<ConversationSummary[]> {
  return sendJson<ConversationSummary[]>("/api/conversations", { signal });
}

export function createConversation(body: NewConversationBody = {}): Promise<ConversationDetail> {
  return sendJson<ConversationDetail>("/api/conversations", { method: "POST", body });
}

export function getConversation(id: number, signal?: AbortSignal): Promise<ConversationDetail> {
  return sendJson<ConversationDetail>(`/api/conversations/${id}`, { signal });
}

export async function deleteConversation(id: number): Promise<void> {
  await send(`/api/conversations/${id}`, { method: "DELETE" });
}

/**
 * What the founder could say next, for the two moments there is no turn stream to
 * carry it: arriving at a conversation, and reloading in the middle of one. The
 * server answers with an empty array rather than an error when it cannot think of
 * anything, so a caller never has to tell "none" from "broken".
 */
export function getSuggestions(id: number, signal?: AbortSignal): Promise<Suggestion[]> {
  return sendJson<Suggestion[]>(`/api/conversations/${id}/suggestions`, { signal });
}

// --- Dossier and export ----------------------------------------------------

export function getDossier(caseId: number | null, signal?: AbortSignal): Promise<Dossier> {
  return sendJson<Dossier>(scoped("/api/dossier", caseId), { signal });
}

/** Markdown comes back as text so the caller can put it on the clipboard. */
export async function exportConversationMarkdown(
  id: number,
  signal?: AbortSignal,
): Promise<string> {
  const res = await send(`/api/conversations/${id}/export.md`, { signal });
  return await res.text();
}

export async function exportDossierMarkdown(
  caseId: number | null,
  signal?: AbortSignal,
): Promise<string> {
  const res = await send(scoped("/api/export.md", caseId), { signal });
  return await res.text();
}

// --- Voice -----------------------------------------------------------------

export async function transcribe(
  audio: Blob,
  conversationId?: number,
  signal?: AbortSignal,
): Promise<TranscriptionResult> {
  const form = new FormData();
  // A MediaRecorder blob carries no filename, and multipart parsers between us and
  // the model are friendlier to a part that has one.
  form.append("audio", audio, "turn.webm");
  // Lets the server bias the ASR with this case's own vocabulary — company names,
  // product names, the terms of the last few findings.
  if (conversationId !== undefined) form.append("conversationId", String(conversationId));
  let res: Response;
  try {
    res = await fetch("/api/voice/transcribe", {
      method: "POST",
      credentials: "same-origin",
      body: form,
      signal: signal ?? null,
    });
  } catch (err) {
    rethrow(err);
  }
  if (!res.ok) throw await readError(res);
  return (await res.json()) as TranscriptionResult;
}

/**
 * A very long reply percent-encodes to a very long URL — Cyrillic runs 5.8 URL
 * characters a glyph — and somewhere past this a proxy answers 431 instead of
 * audio. Below it the GET streams; above it the caller goes straight to the POST
 * path rather than making a request that is built to fail.
 *
 * 1800, not 8000. Node accepts 8KB of request line, but the CDNs and reverse
 * proxies in front of it commonly cut off at 2,048 BYTES — and the fallback for a
 * 431 is a second full synthesis over POST, so every URL between the proxy's limit
 * and this one costs the reply twice at the vendor and delays it by a whole round
 * trip. Measured: English replies land at 452-527 characters and a 400-glyph
 * Russian reply extrapolates to ~2,340, so this is the line between "streams" and
 * "was always going to be POSTed anyway".
 */
const SPEAK_URL_MAX = 1800;

/**
 * The streaming way to say a reply: a same-origin GET the <audio> element loads
 * DIRECTLY as its src, so playback starts on the first chunks instead of after
 * the whole file has downloaded. Cookie auth rides along because it is
 * same-origin. Returns null when the text is too long to fit in a URL; the POST
 * path below still exists for exactly that caller.
 */
export function speakStreamUrl(
  text: string,
  language: Locale,
  conversationId: number | null,
): string | null {
  const query =
    `text=${encodeURIComponent(text)}&language=${encodeURIComponent(language)}` +
    (conversationId === null ? "" : `&conversationId=${conversationId}`);
  const url = `/api/voice/speak?${query}`;
  return url.length > SPEAK_URL_MAX ? null : url;
}

/**
 * Returns an object URL for the spoken reply. The caller owns that URL and must
 * revoke it once playback is done, or a long session holds on to every reply it
 * has ever heard.
 */
export async function speak(text: string, language: Locale, signal?: AbortSignal): Promise<string> {
  const res = await send("/api/voice/speak", {
    method: "POST",
    body: { text, language },
    signal,
  });
  return URL.createObjectURL(await res.blob());
}

// --- The turn stream -------------------------------------------------------

/**
 * POSTs a turn and hands each `TurnEvent` to `onEvent` as it arrives.
 *
 * Resolves when the server closes the stream. Rejects on transport failure, but a
 * failure the server reports in-band arrives as an `error` event and still resolves:
 * from the client's side that turn ended in an orderly way.
 */
export async function streamTurn(
  conversationId: number,
  body: TurnBody,
  onEvent: (event: TurnEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`/api/conversations/${conversationId}/turn`, {
      method: "POST",
      credentials: "same-origin",
      headers: { ...JSON_HEADERS, accept: "text/event-stream" },
      body: JSON.stringify(body),
      signal: signal ?? null,
    });
  } catch (err) {
    rethrow(err);
  }
  await pump(res, onEvent, isTurnEvent);
}

/**
 * Reads one SSE response to the end, handing each parsed frame to `onEvent`.
 *
 * Extracted from `streamTurn` when a second stream arrived (the data chat) rather
 * than written twice: the framing, the chunk-boundary buffering and the abort
 * behaviour here are the parts that were got wrong once already and fixed once, and
 * a copy of them would be a copy of the next bug too. `guard` decides what counts
 * as an event of the caller's own union.
 */
async function pump<T>(
  res: Response,
  onEvent: (event: T) => void,
  guard: (value: unknown) => value is T,
): Promise<void> {
  if (!res.ok) throw await readError(res);
  if (!res.body) throw new ApiError("stream_unsupported", "The reply could not be read.", 0);

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  // Frames are delimited by a blank line, but a network chunk ends wherever the
  // network decides, including between "dat" and "a: {". Whatever follows the last
  // complete frame is carried into the next read rather than parsed.
  let buffer = "";

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      // `stream: true` keeps a multi-byte character that straddles a chunk boundary
      // intact; without it Cyrillic replies grow replacement characters at random
      // offsets.
      buffer += decoder.decode(value, { stream: true });
      buffer = drain(buffer, onEvent, guard);
    }
    buffer += decoder.decode();
    // A stream cut short of its final blank line can still hold one usable frame.
    const tail = drain(buffer, onEvent, guard);
    if (tail.trim().length > 0) dispatch(tail, onEvent, guard);
  } catch (err) {
    rethrow(err);
  } finally {
    // Releasing lets the connection be torn down promptly when the caller aborted
    // mid-frame. On an already-errored stream this itself can throw.
    try {
      reader.releaseLock();
    } catch {
      // The stream is finished with either way.
    }
  }
}

/** Consumes every complete frame in `buffer` and returns the unterminated remainder. */
function drain<T>(
  buffer: string,
  onEvent: (event: T) => void,
  guard: (value: unknown) => value is T,
): string {
  let rest = buffer;
  for (;;) {
    const boundary = findBoundary(rest);
    if (boundary === null) return rest;
    dispatch(rest.slice(0, boundary.index), onEvent, guard);
    rest = rest.slice(boundary.index + boundary.length);
  }
}

/**
 * SSE ends a frame with a blank line, and the spec permits CRLF as well as LF. Which
 * one arrives depends on the proxy in front of us, not on our server.
 */
function findBoundary(buffer: string): { index: number; length: number } | null {
  const lf = buffer.indexOf("\n\n");
  const crlf = buffer.indexOf("\r\n\r\n");
  if (lf === -1 && crlf === -1) return null;
  if (crlf !== -1 && (lf === -1 || crlf < lf)) return { index: crlf, length: 4 };
  return { index: lf, length: 2 };
}

function dispatch<T>(
  frame: string,
  onEvent: (event: T) => void,
  guard: (value: unknown) => value is T,
): void {
  const data: string[] = [];
  for (const line of frame.split(/\r?\n/)) {
    // Skips the ": ping" keepalives and any field we do not use.
    if (line.startsWith(":") || !line.startsWith("data:")) continue;
    const value = line.slice("data:".length);
    data.push(value.startsWith(" ") ? value.slice(1) : value);
  }
  if (data.length === 0) return;

  let parsed: unknown;
  try {
    parsed = JSON.parse(data.join("\n"));
  } catch {
    // One malformed frame must not end a conversation that is otherwise streaming
    // cleanly, so it is dropped rather than thrown.
    return;
  }
  if (guard(parsed)) onEvent(parsed);
}

/**
 * Anything with a string `type`. Deliberately loose: the server may learn a new
 * event before this bundle does — a `tool` frame from a turn that is calling tools
 * is exactly that — and the right answer to one the client has not learned is to
 * hand it to the reducer and let its switch ignore it, not to drop the frame here
 * and not to end the stream.
 */
function isTurnEvent(value: unknown): value is TurnEvent {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { type?: unknown }).type === "string"
  );
}

// --- Asking the collected data ---------------------------------------------
//
// `POST /api/cases/:id/ask` and `POST /api/studies/:id/ask`. A question in words,
// answered ONLY out of what has been collected about that business — the notes,
// the transcripts, the uploaded files, the study themes and their verbatim quotes —
// with every claim carrying a reference to the row it came from. When the record
// does not answer it, it says so instead of reasoning from the model's own
// knowledge; that refusal is the whole value of the surface.

export type AskSourceKind = "finding" | "message" | "document" | "theme" | "quote";

/** One thing the answer cited. `ref` is the bracketed token as it appears in it. */
export interface AskSource {
  ref: string;
  kind: AskSourceKind;
  label: string;
}

export type AskEvent =
  | { type: "thinking" }
  | { type: "delta"; text: string }
  | { type: "done"; answer: string; sources: AskSource[] }
  | { type: "error"; code: string; message: string };

/** The panel's own thread. There is no table behind it; the client holds it. */
export interface AskTurn {
  role: "user" | "assistant";
  content: string;
}

export type AskScope = { kind: "case"; id: number } | { kind: "study"; id: number };

function isAskEvent(value: unknown): value is AskEvent {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { type?: unknown }).type === "string"
  );
}

/**
 * Asks one question and hands each event to `onEvent` as it arrives. Resolves when
 * the server closes the stream; a failure the server reports in-band arrives as an
 * `error` event and still resolves, exactly like a turn.
 */
export async function streamAsk(
  scope: AskScope,
  question: string,
  history: readonly AskTurn[],
  onEvent: (event: AskEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const path = scope.kind === "case" ? `/api/cases/${scope.id}/ask` : `/api/studies/${scope.id}/ask`;
  let res: Response;
  try {
    res = await fetch(path, {
      method: "POST",
      credentials: "same-origin",
      headers: { ...JSON_HEADERS, accept: "text/event-stream" },
      body: JSON.stringify({ question, history }),
      signal: signal ?? null,
    });
  } catch (err) {
    rethrow(err);
  }
  await pump(res, onEvent, isAskEvent);
}

// --- Studies ---------------------------------------------------------------
//
// A study is one question fielded to many people (docs/PLATFORM-W4.md 2.5). It
// belongs to a case, so every ownership check, the dossier and the documents keep
// working, and a respondent's interview is an ordinary conversations row with
// kind="interview".
//
// The wire shapes live in shared/types.ts and are re-exported here so a component
// imports one module. What is declared BELOW rather than there is the synthesis,
// which is the half of the seam this client defines: nothing has answered these
// endpoints yet, and until something does the screen draws its own empty state
// instead of an error.

export type {
  CreatedInvite,
  InviteSummary,
  ParticipantRow,
  ParticipantStatus,
  ParticipantsResponse,
  ParticipantTranscript,
  Study,
  StudyAnonymity,
  StudyAudience,
  StudyStatus,
  StudySummary,
  TopicSpineEntry,
} from "../../../shared/types";

/**
 * A spine entry on its way UP.
 *
 * `TopicSpineEntry.id` is stable and assigned by the server, because coverage is
 * counted against it across every interview in the study — renaming a topic must not
 * silently start a new column in the synthesis. A row the researcher has just typed
 * does not have one yet, so this is the shape that goes in and that is the shape that
 * comes back.
 */
export interface TopicDraft {
  id?: string;
  title: string;
  intent: string;
}

/**
 * The synthesis, mirrored from `server/services/synthesis.ts`.
 *
 * These shapes are declared rather than imported because the web build only sees
 * `web/` and `shared/`, and the report lives in the service. If the server's version
 * moves, this is the file that has to move with it.
 *
 * Two things are deliberately NOT taken from the server even though it sends them.
 * `ThemeReport.count` arrives pre-rendered as "7 of 11" and `SaturationReport
 * .statement` arrives as a finished English sentence; both are re-derived here from
 * the numbers beside them, so the Russian build says "7 из 11" rather than falling
 * back to English at the one place the whole product's credibility sits.
 */

export type Directness = "behaviour" | "opinion" | "hypothetical";
export type Polarity = "supports" | "contradicts" | "neutral";
export type Band = "strong" | "provisional" | "signal";

/** One code applied to one verbatim span said by one person. The unit of evidence. */
export interface EvidenceUnit {
  id: number;
  participantId: number;
  /** "Dana" on a named study, "P3" on an anonymous one. Never both, never null. */
  speaker: string;
  segment: string | null;
  conversationId: number;
  messageId: number;
  saidAt: string;
  codeId: number;
  code: string;
  themeId: number | null;
  theme: string | null;
  /** The exact words. Stored rather than reconstructed, so nothing can paraphrase. */
  quote: string;
  polarity: Polarity;
  directness: Directness;
  /** True when the interviewer said the key term first: a theme put in their mouth. */
  primed: boolean;
}

export interface ThemeReport {
  id: number;
  position: number;
  /** A noun phrase. The claim is `statement`; this is only what to call it. */
  title: string;
  /** One falsifiable sentence in the respondents' register, not the researcher's. */
  statement: string;
  supportN: number;
  /** The denominator: participants whose interview reached this topic at all. */
  askedN: number;
  contraN: number;
  /** The server's own rendering of the count. Re-derived here, for Russian. */
  count: string;
  confidence: number;
  band: Band;
  directnessWeight: number;
  directness: Record<Directness, number>;
  primedFraction: number;
  codeIds: number[];
  quotes: EvidenceUnit[];
  /** Opposing evidence, kept beside it rather than smoothed away. */
  contradictions: EvidenceUnit[];
  /** True when this theme sits below the study's reporting threshold. */
  withheld: boolean;
}

/** One person who said one thing and had already done another. The say-do gap. */
export interface SayDidPair {
  participantId: number;
  speaker: string;
  themeId: number | null;
  theme: string | null;
  said: EvidenceUnit;
  did: EvidenceUnit;
}

/** Two people, one code, opposite polarity. Never averaged. */
export interface Disagreement {
  codeId: number;
  code: string;
  supports: EvidenceUnit[];
  contradicts: EvidenceUnit[];
}

/** A code exactly one person raised. Worth reading, never counted. */
export interface Singleton {
  codeId: number;
  code: string;
  quote: EvidenceUnit;
}

export type HypothesisVerdict = "supported" | "contradicted" | "untested" | "refined";

export interface HypothesisReckoning {
  hypothesis: string;
  verdict: HypothesisVerdict;
  note: string;
  themeIds: number[];
}

/** Guest, Namey and Chen (2020), computed. Never a green tick. */
export interface SaturationReport {
  baseSize: number;
  runLength: number;
  newInfoByRun: { run: number; throughN: number; newCodes: number; newInfoPct: number }[];
  /** First run at or below 5% new codes, as the interview it ended on. */
  saturatedAtN: number | null;
  /** First run with no new codes at all. */
  zeroAtN: number | null;
  degreePct: number | null;
  /** The server's English sentence. This client writes its own, in both languages. */
  statement: string;
  /** True when N is too small for the method to say anything. */
  tooFew: boolean;
}

export interface SynthesisReport {
  runId: number;
  studyId: number;
  studyName: string | null;
  goal: string;
  audience: StudyAudience;
  anonymity: StudyAnonymity;
  minReportN: number;
  targetN: number;
  createdAt: string;
  model: string;
  nInterviews: number;
  /** Completed interviews whose respondent asked for their words to be removed. */
  nWithdrawn: number;
  firstInterviewAt: string | null;
  lastInterviewAt: string | null;
  /** Three sentences, written last, from the strong themes only. */
  answer: string[];
  themes: ThemeReport[];
  sayDid: SayDidPair[];
  disagreements: Disagreement[];
  singletons: Singleton[];
  hypotheses: HypothesisReckoning[];
  saturation: SaturationReport;
  nextQuestions: string[];
}

/**
 * `GET /api/studies/:id/synthesis`.
 *
 * A run is the longest wait in the product, so this carries both the finished report
 * and whether one is in flight. `status` is the server's own English progress prose
 * and is deliberately not rendered: the status line writes its own line, in the
 * language the researcher is reading.
 */
export interface SynthesisState {
  running: boolean;
  status: string;
  startedAt: string | null;
  run: SynthesisReport | null;
}

/** `POST /api/studies/:id/synthesize`. 202 while it works, 200 when one already fits. */
export interface SynthesisStart extends SynthesisState {
  /** True when an existing run already covers every completed interview. */
  reused: boolean;
}

export interface NewStudyBody {
  goal: string;
  name?: string;
  intro?: string;
  audience?: StudyAudience;
  targetN?: number;
  timeBudgetMin?: number;
  hypotheses?: string[];
  guide?: TopicDraft[];
  languages?: string[];
}

export interface StudyPatch {
  name?: string;
  goal?: string;
  intro?: string;
  audience?: StudyAudience;
  targetN?: number;
  timeBudgetMin?: number;
  hypotheses?: string[];
  guide?: TopicDraft[];
  status?: StudyStatus;
}

export interface InviteBody {
  kind?: "open" | "personal";
  label?: string;
  inviteeName?: string;
  maxUses?: number;
  maxTurns?: number;
  expiresInDays?: number;
  voiceEnabled?: boolean;
}

/** What one light-model call turns a question into: a name, beliefs and a spine. */
export interface StudyDraft {
  name: string;
  hypotheses: string[];
  guide: TopicDraft[];
}

export function listStudies(caseId: number, signal?: AbortSignal): Promise<StudySummary[]> {
  return sendJson<StudySummary[]>(`/api/cases/${caseId}/studies`, { signal });
}

export function createStudy(caseId: number, body: NewStudyBody): Promise<StudySummary> {
  return sendJson<StudySummary>(`/api/cases/${caseId}/studies`, { method: "POST", body });
}

/**
 * The topic spine, drafted from the question. One light-model call, asked for
 * explicitly — the researcher edits rather than authors, which is the difference
 * between two minutes and twenty, and nobody waits on a call they did not ask for.
 */
export function draftStudy(
  caseId: number,
  body: { goal: string; audience: StudyAudience; timeBudgetMin: number; language: Locale },
  signal?: AbortSignal,
): Promise<StudyDraft> {
  return sendJson<StudyDraft>(`/api/cases/${caseId}/studies/draft`, {
    method: "POST",
    body,
    signal,
  });
}

export function getStudy(id: number, signal?: AbortSignal): Promise<StudySummary> {
  return sendJson<StudySummary>(`/api/studies/${id}`, { signal });
}

export function updateStudy(id: number, patch: StudyPatch): Promise<StudySummary> {
  return sendJson<StudySummary>(`/api/studies/${id}`, { method: "PATCH", body: patch });
}

/**
 * Mints a link and answers with it ONCE.
 *
 * Only sha256(token) is stored, so this response is the only time the URL exists
 * anywhere the researcher can read it. There is no endpoint that shows it again and
 * that is the design, not an omission — which is why the screen says so in one line
 * instead of offering a button that cannot work.
 */
export function createInvite(studyId: number, body: InviteBody = {}): Promise<CreatedInvite> {
  return sendJson<CreatedInvite>(`/api/studies/${studyId}/invites`, { method: "POST", body });
}

/** The links that exist, without their tokens. Uses, expiry, and whether it is dead. */
export function listInvites(studyId: number, signal?: AbortSignal): Promise<InviteSummary[]> {
  return sendJson<InviteSummary[]>(`/api/studies/${studyId}/invites`, { signal });
}

/**
 * Revoke, not delete. The link stops working and every session it minted ends, while
 * the interviews it produced stay exactly as they were: what somebody already said is
 * not retracted by closing the door behind them.
 */
export function revokeInvite(inviteId: number): Promise<InviteSummary> {
  return sendJson<InviteSummary>(`/api/invites/${inviteId}`, { method: "DELETE" });
}

/**
 * Who has answered, and whether there is enough of it to be worth a run.
 *
 * The route answers with a bare array while the readiness verdict is still being
 * written on the server. Until it lands, this says the honest minimum from the counts
 * it already has rather than leaving the board unable to render — five completed
 * interviews is the line docs/PLATFORM-W4.md draws between "signal" and "a pattern",
 * and the server's own verdict wins the moment it exists.
 */
export async function listParticipants(
  studyId: number,
  signal?: AbortSignal,
): Promise<ParticipantsResponse> {
  const body = await sendJson<ParticipantsResponse | ParticipantRow[]>(
    `/api/studies/${studyId}/participants`,
    { signal },
  );
  if (!Array.isArray(body)) return body;
  const done = body.filter((row) => row.status === "completed").length;
  return {
    participants: body,
    ready: done >= 5,
    readyReason: done >= 5 ? "enough" : "too_few",
  };
}

export function getInterview(
  studyId: number,
  participantId: number,
  signal?: AbortSignal,
): Promise<ParticipantTranscript> {
  return sendJson<ParticipantTranscript>(
    `/api/studies/${studyId}/transcript/${participantId}`,
    { signal },
  );
}

/** 202 and then poll. A run costs real money, so nothing here happens on its own. */
export function synthesize(studyId: number): Promise<SynthesisStart> {
  return sendJson<SynthesisStart>(`/api/studies/${studyId}/synthesize`, { method: "POST" });
}

/** The empty answer: no run, none in flight. A shape this screen draws, not an error. */
const NO_SYNTHESIS: SynthesisState = {
  running: false,
  status: "",
  startedAt: null,
  run: null,
};

export async function getSynthesis(
  studyId: number,
  signal?: AbortSignal,
): Promise<SynthesisState> {
  try {
    return await sendJson<SynthesisState>(`/api/studies/${studyId}/synthesis`, { signal });
  } catch (err) {
    // No run yet is a state this screen draws, not a failure it reports. So is the
    // endpoint not being deployed yet, which is the shape of a parallel build.
    if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
      return NO_SYNTHESIS;
    }
    throw err;
  }
}

export type ArtifactKind =
  | "brief.md"
  | "brief.docx"
  | "evidence.csv"
  | "evidence.xlsx"
  | "study.html";

/**
 * The downloads. Plain hrefs: there is nothing to wait for, so nothing to indicate,
 * and no `download` attribute - it beats the server's own dated filename and the
 * file then arrives named after a database row.
 */
export function artifactUrl(studyId: number, kind: ArtifactKind, locale: Locale): string {
  return `/api/studies/${studyId}/artifacts/${kind}?locale=${locale}`;
}
